import {classes, pending, present} from '@bitstillery/common/lib/utils'
import {unique_id} from '@bitstillery/common/lib/utils'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {nextTick} from '@bitstillery/mithril'
import {modelref_adapter, modelref_assign} from '@bitstillery/common/lib/store'
import {MithrilComponent} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'

import type {Field, ModelRef} from '@bitstillery/common/types/field'
import {failure_message} from '@bitstillery/common/lib/validation'

interface FieldCodeAttrs {
    autofocus?: boolean
    characters?: RegExp
    className?: Field['className']
    disabled?: Field['disabled']
    help?: Field['help']
    icon?: string
    label?: Field['label']
    model?: ModelRef
    onchange?: (value: string) => unknown
    placeholders?: number
    validation?: Field['validation']
}

export class FieldCode extends MithrilComponent<FieldCodeAttrs> {
    id = unique_id()
    _model: (string | null)[] = []
    $input_selectors: NodeListOf<HTMLInputElement> = pending()

    constructor(vnode: m.Vnode<FieldCodeAttrs>) {
        super()
        if (!vnode.attrs.model) {
            return
        }
        const {model_value} = modelref_adapter(vnode.attrs.model)
        const placeholders = vnode.attrs.placeholders ?? 6
        if (model_value && typeof model_value === 'string') {
            this._model = [...model_value]
        } else {
            this._model = Array<string | null>(placeholders).fill(null)
        }
    }

    override oncreate(vnode: m.Vnode<FieldCodeAttrs>) {
        this.$input_selectors = document.querySelectorAll(`.code-${this.id} input`)
        if (vnode.attrs.autofocus) {
            present(this.$input_selectors[0], 'first code input').focus()
        }
    }

    default_behaviour(e: KeyboardEvent) {
        // Focus previous/next input element
        if (['CapsLock', 'Control', 'Delete', 'Tab', 'Shift'].includes(e.key)) {
            return true
        }
        // Allow onpaste event
        if (e.ctrlKey && e.key === 'v') {
            return true
        }
        // Allow select value
        if (e.ctrlKey && e.key === 'a') {
            return true
        }
        return false
    }

    view(vnode: m.Vnode<FieldCodeAttrs>) {
        if (!vnode.attrs.model) {
            return null
        }
        const {model_ref} = modelref_adapter(vnode.attrs.model)
        if (!model_ref) {
            return null
        }
        const set_value = (value: string) => modelref_assign(vnode.attrs.model, value)
        const {validation} = vnode.attrs
        const invalid = validation ? validation._invalid : false
        const {disabled} = vnode.attrs

        return (
            <div
                className={classes('c-field-code', 'field', vnode.attrs.className, {
                    [`code-${this.id}`]: true,
                    disabled: disabled,
                    invalid: invalid && validation?.dirty,
                    valid: !validation || !invalid,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}

                <div className='placeholders'>
                    {(() => {
                        const placeholders: m.Vnode[] = []
                        for (let i = 0; i < Number(vnode.attrs.placeholders); i++) {
                            placeholders.push(
                                <input
                                    className={classes({invalid: !this._model[i]})}
                                    disabled={vnode.attrs.disabled}
                                    onkeydown={async (e: KeyboardEvent) => {
                                        // Allow common keyboard actions
                                        if (this.default_behaviour(e)) {
                                            return
                                        }
                                        if (e.key === 'Backspace') {
                                            e.preventDefault()
                                            this._model[i] = null
                                            set_value(this._model.join(''))
                                            if (i > 0) {
                                                present(this.$input_selectors[i - 1], 'previous code input').focus()
                                            }
                                            return
                                        }

                                        if (e.key === 'ArrowLeft' && i > 0) {
                                            present(this.$input_selectors[i - 1], 'previous code input').focus()
                                        }
                                        if (e.key === 'ArrowRight' && i < Number(vnode.attrs.placeholders) - 1) {
                                            present(this.$input_selectors[i + 1], 'next code input').focus()
                                        }
                                        // Skip function keys that are not catched by default_behaviour and
                                        // Check for an allow-list of characters that may enter the inputs.
                                        if (e.key.length !== 1) {
                                            return
                                        }
                                        if (!vnode.attrs.characters || vnode.attrs.characters.test(e.key)) {
                                            // The boxes are fully controlled from `_model`, so the browser's own
                                            // insertion is pure interference — and it lands in the WRONG box: the
                                            // `await` below suspends the listener, microtasks drain (moving focus
                                            // to i+1) and only then does the default action type the character,
                                            // filling the next box too. Same for backspace, which deletes from the
                                            // box focus has already moved back to.
                                            e.preventDefault()
                                            this._model[i] = e.key.toUpperCase()
                                            const new_value = this._model.join('')
                                            set_value(new_value)

                                            if (i === Number(vnode.attrs.placeholders) - 1) {
                                                if (vnode.attrs.validation && !vnode.attrs.validation.dirty) {
                                                    vnode.attrs.validation.dirty = true
                                                }

                                                if (vnode.attrs.onchange) {
                                                    vnode.attrs.onchange(new_value)
                                                }
                                            }

                                            // Focus next element
                                            await nextTick()
                                            const next_input = this.$input_selectors[i + 1]
                                            if (i < this._model.length - 1 && next_input) {
                                                next_input.focus()
                                            }
                                            return
                                        }

                                        e.preventDefault()
                                    }}
                                    onpaste={(e) => {
                                        e.preventDefault()
                                        const value = (e.clipboardData?.getData('text') ?? '').trim()
                                        // Trimmed code must fit the placeholders or the paste is ignored
                                        if (value.length !== vnode.attrs.placeholders) {
                                            return
                                        }

                                        for (let j = 0; j < vnode.attrs.placeholders; j++) {
                                            this._model[j] = value.charAt(j)
                                        }
                                        const new_value = this._model.join('')
                                        set_value(new_value)
                                        if (vnode.attrs.validation && !vnode.attrs.validation.dirty) {
                                            vnode.attrs.validation.dirty = true
                                        }
                                        if (vnode.attrs.onchange) {
                                            vnode.attrs.onchange(new_value)
                                        }
                                        present(this.$input_selectors[vnode.attrs.placeholders - 1], 'last code input').focus()
                                        return
                                    }}
                                    placeholder={'...'}
                                    type='text'
                                    value={this._model[i]}
                                />,
                            )
                        }
                        return placeholders
                    })()}
                </div>
                {(() => {
                    if (invalid && validation?.dirty) {
                        return <div className='help validation'>{failure_message(invalid)}</div>
                    } else if (vnode.attrs.help) {
                        return <div class='help'>{vnode.attrs.help}</div>
                    }
                    return undefined
                })()}
            </div>
        )
    }
}
